import _ from 'lodash';
import { TRANSCRIPT } from 'common/types';
import { getFullEmployeeName } from 'common/utils/nameUtils';
import { translate } from 'common/utils/i18n';
import server, { csrfFromDom } from 'common/utils/server';
import { notifySuccess, notifyWarning, notifyError } from 'common/state/notificationState';
import { updateConfetti, startReview } from 'common/state/qaAssignments';
import { getAssignment } from 'common/state/qaAssignment';
import * as queryStringUtils from 'common/utils/queryStringUtils';
import { QA_REVIEW_ORIGIN } from 'qa/constants';
import api from '../api';
import { fetchCalibration } from '../CalibrationPage/state';

export const filterTranscript = (transcript, employee, blind) => {
  if (!blind || !employee) return transcript;

  const { first_name: firstName, last_name: lastName } = employee;

  const firstLastName = `${firstName} ${lastName}`;
  const firstLastNameRe = new RegExp(_.escapeRegExp(firstLastName), 'gi');
  const firstNameRe = new RegExp(_.escapeRegExp(firstName), 'gi');
  return transcript
    ?.replace(firstLastNameRe, `${'*'.repeat(firstName.length)} ${'*'.repeat(lastName.length)}`)
    .replace(firstNameRe, '*'.repeat(firstName.length));
};

// Selectors

export const getReview = state => state.qaReview.review;
const isComplete = (progress, reviewerId) =>
  _.get(
    _.find(progress, p => p.employee.id === parseInt(reviewerId)),
    'progress',
    0
  ) >= 100;

// Actions

const FETCH_FORMS_SUCCESS = 'reviewPage/FETCH_FORMS_SUCCESS';
export const FETCH_REVIEW_SUCCESS = 'reviewPage/FETCH_REVIEW_SUCCESS';
export const FETCH_REVIEW_ERROR = 'reviewPage/FETCH_REVIEW_ERROR';
const FETCH_FORM_SUCCESS = 'reviewPage/FETCH_FORM_SUCCESS';
export const FETCH_ANSWERS_SUCCESS = 'reviewPage/FETCH_ANSWERS_SUCCESS';
const UPDATE_ANSWER_SUCCESS = 'reviewPage/UPDATE_ANSWER_SUCCESS';
const CREATE_ANSWER_SUCCESS = 'reviewPage/CREATE_ANSWER_SUCCESS';
const SAVING_ANSWER = 'reviewPage/SAVING_ANSWER';
export const UPDATE_QUESTION_ANSWER = 'reviewPage/UPDATE_QUESTION_ANSWER';
const UPDATE_INTERACTION_ID = 'reviewPage/UPDATE_INTERACTION_ID';
const SEARCH_SOLICITATION = 'reviewPage/SEARCH_SOLICITATION';
const FETCH_INTERACTION_DETAILS = 'reviewPage/FETCH_INTERACTION_DETAILS';
const UPDATE_INTERACTION_LINK = 'reviewPage/UPDATE_INTERACTION_LINK';
const UPDATE_EMPLOYEE_INPUT = 'reviewPage/UPDATE_EMPLOYEE_INPUT';
const SUGGESTION_SELECTED = 'reviewPage/SUGGESTION_SELECTED';
const CREATE_REVIEW_SUCCESS = 'reviewPage/CREATE_REVIEW_SUCCESS';
const ACTIVATE_QUESTION = 'reviewPage/ACTIVATE_QUESTION';
const COMPLETE_REVIEW = 'reviewPage/COMPLETE_REVIEW';
const TRANSCRIPT_REQUEST = 'reviewPage/TRANSCRIPT_REQUEST';
export const UPDATE_TRANSCRIPT = 'reviewPage/UPDATE_TRANSCRIPT';
const UPDATE_RECORDING = 'reviewPage/UPDATE_RECORDING';
const CLEAR = 'reviewPage/CLEAR';
const NEW_ANNOTATION = 'reviewPage/NEW_ANNOTATION';
const REMOVE_ANNOTATION = 'reviewPage/REMOVE_ANNOTATION';
const SAVE_ANNOTATION = 'reviewPage/SAVE_ANNOTATION';
const FETCH_ANNOTATIONS_SUCCESS = 'reviewPage/FETCH_ANNOTATIONS_SUCCESS';
const UPDATE_COUNT_TOWARD_SCORE = 'reviewPage/UPDATE_COUNT_TOWARD_SCORE';
const UPDATE_FILE = 'reviewPage/UPDATE_FILE';
const UPDATE_QUESTION_COMMENT = 'reviewPage/UPDATE_QUESTION_COMMENT';
const UPDATE_ASSIGNMENT = 'reviewPage/UPDATE_ASSIGNMENT';
const LOADING_STARTED = 'reviewPage/LOADING_STARTED';
const LOADING_FINISHED = 'reviewPage/LOADING_FINISHED';
const UPDATE_EVENTS = 'reviewPage/UPDATE_EVENTS';
const SET_ORIGIN = 'reviewPage/SET_ORIGIN';

const UPDATE_HIDE_REVIEWER = 'reviewPage/UPDATE_HIDE_REVIEWER';
export const UPDATE_ACKNOWLEDGEMENT = 'reviewPage/UPDATE_ACKNOWLEDGEMENT';
export const FETCH_CHANNELS_SUCCESS = 'reviewPage/FETCH_CHANNELS_SUCCESS';
export const UPDATE_CHANNEL = 'reviewPage/UPDATE_CHANNEL';
export const ANSWER_STATUSES = {
  LOADING: 'loading',
  SAVED: 'saved',
};
const EVENTS_LOADING_STARTED = 'reviewPage/EVENTS_LOADING_STARTED';
const EVENTS_LOADING_FINISHED = 'reviewPage/EVENTS_LOADING_FINISHED';

// Action Creators

export const loadingFinished = () => ({
  type: LOADING_FINISHED,
});

export const loadingStarted = () => ({
  type: LOADING_STARTED,
});

export const setOrigin = origin => ({
  type: SET_ORIGIN,
  origin,
});

export const updateCountTowardScore = countTowardScore => ({
  type: UPDATE_COUNT_TOWARD_SCORE,
  countTowardScore,
});

export const updateHideReviewer = hideReviewer => ({
  type: UPDATE_HIDE_REVIEWER,
  hideReviewer,
});

export const updateFile = ({ fileName, keyName, interaction }) => ({
  type: UPDATE_FILE,
  fileName,
  keyName,
  interaction,
});

export const newAnnotation = annotation => ({
  type: NEW_ANNOTATION,
  annotation: { ...annotation, editable: true },
});

const removeAnnotation = annotation => ({
  type: REMOVE_ANNOTATION,
  annotation,
});

export const updateAnnotation = annotation => ({
  type: SAVE_ANNOTATION,
  annotation: { ...annotation },
});

export const editAnnotation = annotation => ({
  type: SAVE_ANNOTATION,
  annotation: { ...annotation, editable: true, storedState: annotation },
});

export const cancelAnnotation = annotation => ({
  type: SAVE_ANNOTATION,
  annotation: { ...annotation.storedState, editable: false },
});

const saveAnnotationSuccess = annotation => ({
  type: SAVE_ANNOTATION,
  annotation: { ...annotation, editable: false },
});

const fetchAnnotationsSuccess = annotations => ({
  type: FETCH_ANNOTATIONS_SUCCESS,
  annotations,
});

const fetchFormsSuccess = json => ({
  type: FETCH_FORMS_SUCCESS,
  forms: json,
});

const fetchReviewSuccess = json => ({
  type: FETCH_REVIEW_SUCCESS,
  review: json,
});

const fetchReviewError = () => ({
  type: FETCH_REVIEW_ERROR,
});

const createReviewSuccess = json => ({
  type: CREATE_REVIEW_SUCCESS,
  review: json,
});

const fetchFormSuccess = json => ({
  type: FETCH_FORM_SUCCESS,
  form: json,
});

const fetchAnswersSuccess = json => ({
  type: FETCH_ANSWERS_SUCCESS,
  answers: json,
});

const createAnswerSuccess = json => ({
  type: CREATE_ANSWER_SUCCESS,
  answer: json,
});

const updateAnswerSuccess = json => ({
  type: UPDATE_ANSWER_SUCCESS,
  answer: json,
});

const savingAnswer = questionId => ({
  type: SAVING_ANSWER,
  questionId,
});

export const suggestionSelected = employee => ({
  type: SUGGESTION_SELECTED,
  employee,
});

export const updateEmployeeInput = employeeInput => ({
  type: UPDATE_EMPLOYEE_INPUT,
  employeeInput,
});

export const updateInteractionId = value => ({
  type: UPDATE_INTERACTION_ID,
  interactionId: value,
});

export const updateInteractionLink = value => ({
  type: UPDATE_INTERACTION_LINK,
  interactionLink: value,
});

const searchSolicitationSuccess = solicitation => ({
  type: SEARCH_SOLICITATION,
  solicitation,
});

const fetchInteractionDetailsSuccess = solicitation => ({
  type: FETCH_INTERACTION_DETAILS,
  solicitation,
});

export const activateQuestion = questionId => ({
  type: ACTIVATE_QUESTION,
  questionId,
});

const completeReviewSuccess = json => ({
  type: COMPLETE_REVIEW,
  review: json,
});

export const clear = () => ({
  type: CLEAR,
});

const updateQuestionAnswer = (questionId, optionId, na) => ({
  type: UPDATE_QUESTION_ANSWER,
  questionId,
  optionId,
  na,
});

const transcriptRequest = ({ loading }) => ({
  type: TRANSCRIPT_REQUEST,
  loading,
});

export const updateTranscript = transcript => ({
  type: UPDATE_TRANSCRIPT,
  transcript,
});

export const updateRecording = items => ({
  type: UPDATE_RECORDING,
  items,
});

const updateQuestionComment = (questionId, comments) => ({
  type: UPDATE_QUESTION_COMMENT,
  comments,
  questionId,
});

export const updateAssignment = (qaAssignmentId, qaAllocationId) => ({
  type: UPDATE_ASSIGNMENT,
  qaAssignmentId,
  qaAllocationId,
});

const updateEvents = snapshot => ({
  type: UPDATE_EVENTS,
  snapshot,
});

const updateAcknowledgementStatus = acknowledgeStatus => ({
  type: UPDATE_ACKNOWLEDGEMENT,
  acknowledgeStatus,
});

const fetchChannelsSuccess = json => {
  const { channels } = json;
  return {
    type: FETCH_CHANNELS_SUCCESS,
    channels,
  };
};

export const updateChannel = channel => ({
  type: UPDATE_CHANNEL,
  channel,
});

export const eventsLoadingStarted = () => ({
  type: EVENTS_LOADING_STARTED,
});

export const eventsLoadingFinished = () => ({
  type: EVENTS_LOADING_FINISHED,
});

// Thunks

export const fetchEvents = id => dispatch => {
  dispatch(eventsLoadingStarted());

  return api
    .fetch(dispatch, `interaction_snapshots/${id}?unscope=true`)
    .then(([json]) => dispatch(updateEvents(json)))
    .finally(() => {
      dispatch(eventsLoadingFinished());
    });
};

export const deleteAnnotation = annotation => dispatch => {
  dispatch(removeAnnotation(annotation));

  if (!annotation.id) {
    dispatch(removeAnnotation(annotation));
    return;
  }

  api
    .fetch(dispatch, `qa_annotations/${annotation.id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-token': csrfFromDom(),
      },
    })
    .then(() => {
      dispatch(removeAnnotation(annotation));
      dispatch(notifySuccess('Annotation deleted!'));
    });
};

const fetchAnnotations = (id, hasTranscript, hasFile, completedAt) => dispatch =>
  api.fetch(dispatch, `qa_annotations?qa_review_id=${id}`).then(([json]) => {
    dispatch(fetchAnnotationsSuccess(json));
    const isComplete = !!completedAt;
    const showInitialNote = !json.length && !hasTranscript && !hasFile;

    if (showInitialNote && !isComplete) {
      dispatch(
        newAnnotation({
          content: '',
          media_type: null,
          start: null,
          stop: null,
        })
      );
    }
  });

export const saveAnnotation = annotation => (dispatch, getState) => {
  const {
    id,
    start,
    stop,
    content,
    comments,

    media_type,

    interaction_event_id,
  } = annotation;
  const review = getReview(getState());
  const isNote = !review.has_transcript && !review.has_file;
  const msg = isNote ? 'Notes saved!' : 'Annotation saved!';

  const method = id ? 'PUT' : 'POST';
  const url = id ? `qa_annotations/${id}` : 'qa_annotations';

  api
    .fetch(dispatch, url, {
      method,
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
      body: JSON.stringify({
        qa_review_id: review.id,
        start,
        stop,
        comments,
        content,
        media_type,
        interaction_event_id,
      }),
    })
    .then(([json]) => {
      dispatch(saveAnnotationSuccess(json));
      dispatch(notifySuccess(msg));
    });
};

export const completeReview =
  (history, redirect = true) =>
  (dispatch, getState) => {
    const {
      qaReview: { review, countTowardScore, isCalibration, hideReviewer },
    } = getState();

    return api
      .fetch(dispatch, `qa_reviews/${review.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'X-CSRF-token': csrfFromDom() },
        body: JSON.stringify({
          completed_at: new Date().getTime(),
          count_toward_score: countTowardScore,
          hide_reviewer: hideReviewer,
        }),
      })
      .then(([json]) => {
        dispatch(completeReviewSuccess(json));
        if (!redirect) return;

        if (isCalibration) {
          history.push('/qa/calibrations');
        } else {
          history.push('/qa/reports/reviews');
        }
      });
  };

export const acknowledgeReview = () => (dispatch, getState) => {
  const review = getReview(getState());

  return api
    .fetch(dispatch, `qa_reviews/${review.id}/acknowledge`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
    })
    .then(([json]) => {
      dispatch(updateAcknowledgementStatus(json));
    })
    .catch(() => {
      dispatch(notifyError(translate('qa.reviews.acknowledge.error')));
    });
};

export const editReview = () => (dispatch, getState) => {
  const review = getReview(getState());

  api
    .fetch(dispatch, `qa_reviews/${review.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
      body: JSON.stringify({
        completed_at: null,
      }),
    })
    .then(([json]) => {
      dispatch(completeReviewSuccess(json));
      dispatch(fetchAnnotations(json.id, json.has_transcript, json.has_file, json.completed_at));
    });
};

const fetchForm =
  (id, calibrationId = null) =>
  dispatch => {
    if (calibrationId) {
      return api
        .fetch(dispatch, `qa_calibrations/${calibrationId}/form`)
        .then(([json]) => dispatch(fetchFormSuccess(json)));
    }
    return api.fetch(dispatch, `qa_forms/${id}`).then(([json]) => dispatch(fetchFormSuccess(json)));
  };

const fetchAnswers = id => dispatch =>
  api
    .fetch(dispatch, `qa_answers?qa_review_id=${id}`)
    .then(([json]) => dispatch(fetchAnswersSuccess(json)));

export const fetchReview = id => dispatch =>
  api
    .fetch(dispatch, `qa_reviews/${id}`)
    .then(([json]) => {
      const promises = [
        dispatch(fetchReviewSuccess(json)),
        dispatch(fetchForm(json.qa_form_id, json.qa_calibration_id)),
      ];
      if (json.qa_calibration_id) {
        promises.push(dispatch(fetchCalibration(json.qa_calibration_id)));
      }
      promises.push(dispatch(fetchAnswers(json.id)));
      promises.push(
        dispatch(fetchAnnotations(json.id, json.has_transcript, json.has_file, json.completed_at))
      );
      return Promise.all(promises);
    })
    .catch(() => {
      dispatch(fetchReviewError());
    });

const pollTranscript = (url, dispatch) => api.fetch(dispatch, url);

export const pullTranscript = (extId, channelId) => (dispatch, getState) => {
  const channelSelectEnabled = getState().companyFeatures.multiple_qa_integrations;

  dispatch(transcriptRequest({ loading: true }));
  const url =
    channelSelectEnabled && channelId ?
      `external_transcripts?interaction_id=${extId}&channel_id=${channelId}`
    : `external_transcripts?interaction_id=${extId}`;
  const pollEvery = 1000;
  const pollUntil = 2 * 60 * 1000;
  clearTimeout(window.transcriptTimeout);

  const interval = setInterval(() => {
    pollTranscript(url, dispatch)
      .then(([json, response]) => {
        if (response.status === 202) return;
        if (json.integrated === false || !!json.error) {
          clearInterval(interval);

          clearTimeout(window.transcriptTimeout);
          dispatch(transcriptRequest({ loading: false }));
          if (json.error) {
            dispatch(notifyError(translate('qa.transcript.fetchError')));
            dispatch(updateTranscript(''));
          }
          return;
        }
        dispatch(updateTranscript(json.transcript));
        dispatch(
          updateFile({
            fileName: json.filename,
            keyName: json.key,
            interaction: json.interaction,
          })
        );
        if (json.interaction && json.interaction.interaction_snapshot_id) {
          dispatch(notifySuccess('Transcript found!'));
        }
        dispatch(transcriptRequest({ loading: false }));
        clearInterval(interval);
      })
      .catch(() => {
        clearInterval(interval);
        dispatch(notifyError(translate('qa.transcript.fetchError')));
        dispatch(updateTranscript(''));
      });
  }, pollEvery);
  window.transcriptInterval = interval;

  window.transcriptTimeout = setTimeout(() => {
    dispatch(transcriptRequest({ loading: false }));
    dispatch(
      notifyError(
        "There was an error fetching this interaction's transcript. Please try again later."
      )
    );
    clearInterval(interval);
  }, pollUntil);
};

export const fetchInteractionDetails = interactionId => (dispatch, getState) => {
  if (!interactionId.trim()) return {};

  const url = `interactions/${encodeURIComponent(interactionId)}`;

  return api
    .fetch(dispatch, url)
    .then(([interaction]) => {
      const channelSelectEnabled =
        getState().featureFlags.qa_review_creation_channel_select ||
        getState().companyFeatures.multiple_qa_integrations;

      if (channelSelectEnabled) {
        dispatch(updateChannel(interaction.channel_id));
      }
      dispatch(notifySuccess('Interaction found!'));

      return interaction;
    })
    .catch(() => {
      dispatch(notifyWarning('No interaction found for that id!'));
    })
    .then(interaction => {
      dispatch(fetchInteractionDetailsSuccess(interaction));
      dispatch(pullTranscript(interactionId, interaction?.channel_id));
    });
};

const getSolicitation = (id, useProxyApi) => (dispatch, getState) => {
  const url = useProxyApi ? `solicitation_references/${id}` : `solicitations/${id}`;

  return api.fetch(dispatch, url).then(([json]) => {
    const channelSelectEnabled = getState().featureFlags.qa_review_creation_channel_select;

    dispatch(searchSolicitationSuccess(json));
    dispatch(pullTranscript(json.ext_id_for_interaction, json.channel_id));

    if (channelSelectEnabled) {
      dispatch(updateChannel(json.channel_id));
    }
  });
};

// `scope=selection` because this list only ever fills a scorecard picker for new
// work; an existing review resolves its own scorecard through `fetchForm`, so a
// review already sitting on a draft or archived scorecard still renders.
export const fetchForms = (reviewId, solicitationId, fetchSolicitationFromProxyApi) => dispatch => {
  dispatch(loadingStarted());

  return api.fetch(dispatch, 'qa_forms?scope=selection').then(([json]) => {
    let rv;
    dispatch(fetchFormsSuccess(json.qa_forms));
    if (reviewId === 'new') {
      if (solicitationId) {
        rv = dispatch(getSolicitation(solicitationId, fetchSolicitationFromProxyApi));
      } else {
        rv = Promise.resolve();
      }
    } else {
      rv = dispatch(fetchReview(reviewId));
    }
    return rv.then(() => dispatch(loadingFinished()));
  });
};

export const fetchAuditForms = reviewId => dispatch => {
  dispatch(loadingStarted());

  return api.fetch(dispatch, 'qa_forms?type=audit&scope=selection').then(([json]) => {
    dispatch(fetchFormsSuccess(json.qa_forms));

    const rv = dispatch(fetchReview(reviewId));

    return rv.then(() => dispatch(loadingFinished()));
  });
};

export const selectForm = formId => dispatch => dispatch(fetchForm(formId));

const revertQuestionAnswer = (questionId, currentAnswer) => dispatch =>
  dispatch(updateQuestionAnswer(questionId, currentAnswer, null));

const answerSaveQueues = new Map();

// Test-only reset so queued saves do not leak across Jest examples.
export const resetAnswerSaveQueues = () => {
  answerSaveQueues.clear();
};

const saveQueueKey = (reviewId, questionId) => `${reviewId}:${questionId}`;

const enqueueQuestionSave = (reviewId, questionId, task) => {
  const key = saveQueueKey(reviewId, questionId);
  const previous = answerSaveQueues.get(key) || Promise.resolve();
  const next = previous.catch(() => {}).then(() => task());

  answerSaveQueues.set(key, next);

  return next.finally(() => {
    if (answerSaveQueues.get(key) === next) {
      answerSaveQueues.delete(key);
    }
  });
};

const getAnswerByQuestionId = (answers, questionId) =>
  _.find(answers, { qa_question_id: questionId });

const buildCommentSavePayload = (getState, reviewId, questionId, comments) => {
  const qaReview = getState().qaReview;
  const savedAnswer = getAnswerByQuestionId(qaReview.answers, questionId);
  const selectedOption = _.get(qaReview.questionAnswers, questionId);

  if (selectedOption) {
    const na = selectedOption === 'na';

    return {
      reviewId,
      questionId,
      optionId: na ? null : selectedOption,
      comments,
      answered: true,
    };
  }

  if (savedAnswer) {
    return {
      reviewId,
      questionId,
      optionId: savedAnswer.qa_option_id,
      comments,
      answered: savedAnswer.answered,
    };
  }

  return {
    reviewId,
    questionId,
    optionId: null,
    comments,
    answered: false,
  };
};

const persistAnswerSave = (
  dispatch,
  getState,
  { reviewId, questionId, optionId, comments, answered },
  revertOption
) => {
  const answer = getAnswerByQuestionId(getState().qaReview.answers, questionId);

  if (answer?.id) {
    return api
      .fetch(dispatch, `qa_answers/${answer.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-token': csrfFromDom(),
        },
        body: JSON.stringify({
          qa_option_id: optionId,
          qa_review_id: reviewId,
          qa_question_id: questionId,
          answered,
          comments,
        }),
      })
      .then(response => {
        if (response) {
          const [json] = response;
          dispatch(updateAnswerSuccess(json));
        } else {
          dispatch(revertQuestionAnswer(questionId, revertOption));
        }
      })
      .catch(() => {
        dispatch(revertQuestionAnswer(questionId, revertOption));
        dispatch(
          notifyError('Sorry, there was an error while updating your answer. Please try again.')
        );
      });
  }

  return createAnswer(dispatch, reviewId, questionId, optionId, comments, answered, revertOption);
};

const createAnswer = (
  dispatch,
  reviewId,
  questionId,
  optionId,
  comments,
  answered,
  revertOption = null
) =>
  api
    .fetch(dispatch, 'qa_answers', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
      body: JSON.stringify({
        qa_option_id: optionId,
        qa_review_id: reviewId,
        qa_question_id: questionId,
        answered,
        comments,
      }),
    })
    .then(response => {
      if (response) {
        const [json] = response;
        dispatch(createAnswerSuccess(json));
      } else {
        dispatch(revertQuestionAnswer(questionId, revertOption));
      }
    })
    .catch(() => {
      dispatch(
        notifyError('Sorry, there was an error while saving your answer. Please try again.')
      );
      dispatch(revertQuestionAnswer(questionId, revertOption));
    });

export const clickedSelectAnotherInteraction =
  ({ history, employeeId }) =>
  async (dispatch, getState) => {
    const { solicitationId, skippedSolicitationIds } = getState().qaReview;
    const queryString = queryStringUtils.createQueryString({
      skippedSolicitationIds: [solicitationId, ...skippedSolicitationIds],
    });

    const response = await server.get(
      `/api/v1/review_recommendations/${employeeId}/random?${queryString}`
    );
    const nextSolicitationId = response.solicitation_id;

    if (!nextSolicitationId) {
      dispatch(notifyError('Return to Stream tab to find more interactions.'));
    }
    dispatch({
      type: 'INITIATED_SELECT_ANOTHER_INTERACTION',
    });
    const currentSearch = queryStringUtils.parseQueryString(history.location.search);
    const newSearch = queryStringUtils.createQueryString({
      ...currentSearch,
      solicitation_id: nextSolicitationId,
    });
    history.replace({ search: newSearch });
    dispatch(fetchForms('new', nextSolicitationId));
  };

export const createReview =
  ({
    history,
    location,
    formId,
    employeeId,
    solicitationId,
    interactionId,
    interactionLink,
    transcript,
    keyName,
    qaAssignmentId,
    qaAllocationId,
    interaction,
    channel,
    origin,
    reviewSimilar,
  }) =>
  dispatch => {
    dispatch({ type: LOADING_STARTED });
    return api
      .fetch(dispatch, 'qa_reviews', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-token': csrfFromDom() },
        body: JSON.stringify({
          transcript,
          qa_form_id: formId,
          employee_id: employeeId,
          solicitation_id: solicitationId,
          interaction_id: interactionId,
          interaction_url: interactionLink,
          filename: keyName,
          qa_assignment_id: qaAssignmentId,
          qa_allocation_id: qaAllocationId,
          interaction,
          channel_id: channel,
          origin,
          partially_matched: reviewSimilar,
        }),
      })
      .then(([json]) => {
        dispatch(createReviewSuccess(json));
        dispatch(fetchAnnotations(json.id));
        history.replace({
          pathname: `/qa/reviews/${json.id}`,
          search: location.search,
        });
      })
      .catch(() => dispatch(loadingFinished()));
  };

export const discardReview = history => (dispatch, getState) => {
  const review = getReview(getState());
  if (review?.id) {
    api
      .fetch(dispatch, `qa_reviews/${review.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'X-CSRF-token': csrfFromDom() },
        body: JSON.stringify({
          deleted_at: new Date().getTime(),
        }),
      })
      .then(() => {
        history.push('/qa/reports/reviews');
        dispatch(notifySuccess('Review deleted successfully'));
      });
  } else {
    dispatch(
      notifyError('Sorry, there was an error while deleting your review. Please try again.')
    );
  }
};

export const saveOption =
  (reviewId, questionId, optionId, answerId, comments, answered, na = false) =>
  (dispatch, getState) => {
    const { questionAnswers } = getState().qaReview;
    const revertOption = questionAnswers[questionId] || null;
    const payload = { reviewId, questionId, optionId, comments, answered };

    dispatch(savingAnswer(questionId));
    dispatch(updateQuestionAnswer(questionId, optionId, na));

    return enqueueQuestionSave(reviewId, questionId, () =>
      persistAnswerSave(dispatch, getState, payload, revertOption)
    );
  };

export const updateComment = (questionId, answer, comments) => (dispatch, getState) => {
  const state = getState();
  const review = getReview(state);

  dispatch(updateQuestionComment(questionId, comments));

  if (answer) {
    const questionAnswers = _.get(state.qaReview, 'questionAnswers', {});
    const na = questionAnswers[questionId] === 'na';

    return dispatch(
      saveOption(
        answer.qa_review_id,
        answer.qa_question_id,
        answer.qa_option_id,
        answer.id,
        comments,
        answer.answered,
        na
      )
    );
  }

  const currentOptionSelection = _.get(getState().qaReview.questionAnswers, questionId, null);

  return enqueueQuestionSave(review.id, questionId, () =>
    persistAnswerSave(
      dispatch,
      getState,
      buildCommentSavePayload(getState, review.id, questionId, comments),
      currentOptionSelection
    )
  );
};

export const nextReviewInAssignment =
  (history, qaAssignmentId, reviewerId, defaultFormId = null, employeeId, reviewSimilar = false) =>
  dispatch =>
    dispatch(getAssignment(qaAssignmentId)).then(([json]) => {
      const complete = isComplete(json.progress_by_reviewer, reviewerId);
      if (complete) {
        dispatch(updateConfetti(false));
        history.push(`/qa/assignments/${qaAssignmentId}/completed`);
      } else {
        dispatch(startReview(qaAssignmentId, defaultFormId, employeeId)).then(async foundReview => {
          if (foundReview) return;

          const reviewSimilarURL = `/qa/assignments/${qaAssignmentId}/review_similar`;
          if (employeeId) {
            history.push(`/qa/assignments/${qaAssignmentId}/retry?employee_id=${employeeId}`);
          } else if (reviewSimilar) {
            const foundSimilarReview = await dispatch(
              startReview(qaAssignmentId, defaultFormId, employeeId, true)
            );
            if (!foundSimilarReview) {
              history.push(reviewSimilarURL);
            }
          } else {
            history.push(reviewSimilarURL);
          }
        });
      }
    });

export const completeReviewInAssignment = (history, qaAssignmentId, reviewerId) => dispatch =>
  dispatch(completeReview(history, false)).then(() =>
    dispatch(getAssignment(qaAssignmentId)).then(([json]) => {
      const complete = isComplete(json.progress_by_reviewer, reviewerId);
      if (complete) {
        dispatch(updateConfetti(false));
        return history.push(`/qa/assignments/${qaAssignmentId}/completed`);
      }
      return history.push('/qa/assignments');
    })
  );

export const fetchChannels = () => dispatch =>
  api.fetch(dispatch, 'channels').then(([json]) => {
    return dispatch(fetchChannelsSuccess(json));
  });

const annotationsEqual = (a1, a2) =>
  a1.start === a2.start && a1.stop === a2.stop && a1.media_type === a2.media_type;

// Selectors

export const getReviewFormQuestions = state => {
  return state.qaReview.form ?
      _.flatMap(state.qaReview.form.sections, section => section.questions)
    : [];
};

// Reducer

const initialClearableState = {
  review: null,
  fetchReviewError: false,
  form: null,
  forms: [],
  answers: [],
  questionStatuses: {},
  employee: null,
  employeeInput: '',
  interactionId: '',
  interactionLink: '',
  solicitationId: null,
  activeQuestion: null,
  questionAnswers: {},
  questionComments: {},
  transcript: '',
  interactionMediaType: TRANSCRIPT,
  recording: null,
  annotations: [],
  countTowardScore: true,
  hideReviewer: false,
  transcriptLoading: false,
  transcriptNotIntegrated: true,
  fileName: null,
  keyName: null,
  isCalibration: false,
  qaAssignmentId: null,
  qaAllocationId: null,
  loading: false,
  blind: false,
  interactionEvents: [],
  interaction: {},
  channels: [],
  channel: null,
  eventsLoading: false,
  origin: QA_REVIEW_ORIGIN.initial,
};

const initialUnclearableState = {
  skippedSolicitationIds: [],
};

const initialState = {
  ...initialClearableState,
  ...initialUnclearableState,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_HIDE_REVIEWER: {
      return { ...state, hideReviewer: action.hideReviewer };
    }
    case UPDATE_EVENTS: {
      return {
        ...state,
        interactionEvents: action.snapshot.interaction_events,
        interaction: action.snapshot.interaction,
      };
    }
    case EVENTS_LOADING_STARTED: {
      return { ...state, eventsLoading: true };
    }
    case EVENTS_LOADING_FINISHED: {
      return { ...state, eventsLoading: false };
    }
    case LOADING_FINISHED: {
      return { ...state, loading: false };
    }
    case LOADING_STARTED: {
      return { ...state, loading: true };
    }
    case UPDATE_COUNT_TOWARD_SCORE: {
      const { countTowardScore } = action;
      return { ...state, countTowardScore };
    }
    case UPDATE_FILE: {
      const { keyName, fileName, interaction } = action;
      const newInteraction = interaction || state.interaction;
      return {
        ...state,
        keyName,
        fileName,
        interaction: newInteraction,
      };
    }
    case NEW_ANNOTATION: {
      const filteredAnnotations = state.annotations.filter(a => !!a.id);
      const annotations = _.concat([action.annotation], filteredAnnotations);
      return { ...state, annotations };
    }
    case REMOVE_ANNOTATION: {
      const annotations = _.filter(state.annotations, a => !annotationsEqual(action.annotation, a));
      return { ...state, annotations };
    }
    case SAVE_ANNOTATION: {
      const annotations = state.annotations.map(a => {
        if (annotationsEqual(a, action.annotation)) {
          return action.annotation;
        }
        return a;
      });
      return { ...state, annotations };
    }
    case FETCH_ANNOTATIONS_SUCCESS: {
      return { ...state, annotations: action.annotations };
    }
    case FETCH_FORMS_SUCCESS: {
      return { ...state, forms: action.forms };
    }
    case FETCH_REVIEW_SUCCESS: {
      const {
        employee,
        interaction_url: interactionLink,
        interaction_id: interactionId,
        solicitation_id: solicitationId,
        count_toward_score: countTowardScore,
        hide_reviewer: hideReviewer,
        qa_allocation_id: qaAllocationId,
        qa_assignment_id: qaAssignmentId,
        partially_matched: partiallyMatched,
        blind,
      } = action.review;

      return {
        ...state,
        employee,
        interactionId,
        interactionLink,
        solicitationId,
        qaAssignmentId,
        qaAllocationId,
        fetchReviewError: false,
        review: {
          ...action.review,
        },
        isCalibration: !!action.review.qa_calibration_id,
        employeeInput: getFullEmployeeName(employee),
        countTowardScore: _.isNil(countTowardScore) ? true : countTowardScore,
        hideReviewer: _.isNil(hideReviewer) ? false : hideReviewer,
        partiallyMatched,
        blind,
      };
    }
    case FETCH_REVIEW_ERROR: {
      return { ...state, review: null, fetchReviewError: true };
    }
    case FETCH_FORM_SUCCESS: {
      /*
      This is a bit confusing because fetching != selecting,
      but here we select on fetch success.
      */
      return { ...state, form: action.form };
    }
    case FETCH_ANSWERS_SUCCESS: {
      const questionAnswers = _.reduce(
        action.answers,
        (accum, answer) => {
          if (answer.answered) {
            return {
              ...accum,
              [answer.qa_question_id]: answer.qa_option_id || 'na',
            };
          }
          return accum;
        },
        {}
      );

      return { ...state, answers: action.answers, questionAnswers };
    }
    case UPDATE_QUESTION_ANSWER: {
      const optionId = action.na ? 'na' : action.optionId;
      const questionAnswers = {
        ...state.questionAnswers,
        [action.questionId]: optionId,
      };
      return { ...state, questionAnswers };
    }
    case UPDATE_ANSWER_SUCCESS: {
      const newQuestionStatuses = {
        ...state.questionStatuses,
        [action.answer.qa_question_id]: ANSWER_STATUSES.SAVED,
      };
      const answers = state.answers.map(answer => {
        if (answer.id === action.answer.id) {
          return action.answer;
        }
        return answer;
      });
      return { ...state, answers, questionStatuses: newQuestionStatuses };
    }
    case CREATE_ANSWER_SUCCESS: {
      const newQuestionStatuses = {
        ...state.questionStatuses,
        [action.answer.qa_question_id]: ANSWER_STATUSES.SAVED,
      };
      const newAnswers = _.concat([action.answer], state.answers);
      return {
        ...state,
        answers: newAnswers,
        questionStatuses: newQuestionStatuses,
      };
    }
    case SAVING_ANSWER: {
      const newQuestionStatuses = {
        ...state.questionStatuses,
        [action.questionId]: ANSWER_STATUSES.LOADING,
      };
      return { ...state, questionStatuses: newQuestionStatuses };
    }

    case UPDATE_EMPLOYEE_INPUT: {
      return { ...state, employeeInput: action.employeeInput };
    }
    case SUGGESTION_SELECTED: {
      return { ...state, employee: action.employee };
    }
    case SEARCH_SOLICITATION: {
      const {
        employee: foundEmployee,
        external_url: interactionLink,
        ext_id_for_interaction: interactionId,
        id: solicitationId,
      } = action.solicitation || {};
      const employeeInput =
        foundEmployee ? getFullEmployeeName(foundEmployee) : state.employeeInput;
      const employee = foundEmployee || state.employee;
      return {
        ...state,
        employee,
        employeeInput,
        solicitationId,
        interactionId: interactionId || state.interactionId,
        interactionLink: interactionLink || state.interactionLink,
      };
    }
    case UPDATE_INTERACTION_ID: {
      return {
        ...state,
        interactionId: action.interactionId,
        searched: false,
      };
    }
    case FETCH_INTERACTION_DETAILS: {
      const {
        employee: foundEmployee,
        external_url: interactionLink,
        ext_interaction_id: interactionId,
      } = action.solicitation || {};
      const employeeInput =
        foundEmployee ? getFullEmployeeName(foundEmployee) : state.employeeInput;
      const employee = foundEmployee || state.employee;
      return {
        ...state,
        employee,
        employeeInput,
        interactionId: interactionId || state.interactionId,
        interactionLink: interactionLink || state.interactionLink,
      };
    }
    case UPDATE_INTERACTION_LINK: {
      return { ...state, interactionLink: action.interactionLink };
    }
    case CREATE_REVIEW_SUCCESS: {
      return { ...state, review: action.review };
    }
    case ACTIVATE_QUESTION: {
      return { ...state, activeQuestion: action.questionId };
    }
    case COMPLETE_REVIEW: {
      return { ...state, review: action.review };
    }
    case TRANSCRIPT_REQUEST: {
      return { ...state, transcriptLoading: action.loading };
    }
    case UPDATE_TRANSCRIPT: {
      return {
        ...state,
        transcript: filterTranscript(action.transcript, state.employee, state.blind),
      };
    }
    case UPDATE_RECORDING: {
      const firstItem = action.items[0];
      const file = firstItem ? firstItem.getAsFile() : null;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      return { ...state, recording: reader.result };
    }
    case CLEAR: {
      return { ...state, ...initialClearableState };
    }
    case UPDATE_QUESTION_COMMENT: {
      const questionComments = _.merge({}, state.questionComments, {
        [action.questionId]: action.comments,
      });
      return { ...state, questionComments };
    }
    case UPDATE_ASSIGNMENT: {
      return {
        ...state,
        qaAllocationId: action.qaAllocationId,
        qaAssignmentId: action.qaAssignmentId,
      };
    }
    case 'INITIATED_SELECT_ANOTHER_INTERACTION': {
      const { solicitationId, skippedSolicitationIds } = state;
      return {
        ...state,
        skippedSolicitationIds: [...skippedSolicitationIds, solicitationId],
      };
    }
    case UPDATE_ACKNOWLEDGEMENT: {
      const { review } = state;
      return {
        ...state,
        review: {
          ...review,
          ...action.acknowledgeStatus,
        },
      };
    }
    case FETCH_CHANNELS_SUCCESS: {
      const { channels } = action;
      return {
        ...state,
        channels,
      };
    }
    case UPDATE_CHANNEL: {
      const { channel } = action;
      return {
        ...state,
        channel,
      };
    }
    case SET_ORIGIN: {
      return { ...state, origin: action.origin };
    }
    default: {
      return state;
    }
  }
};
